import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import './InventoryPage.css';
import RankSkinFrame from '../../RankSkinFrame/RankSkinFrame';
import SkinRankMark from '../../SkinRankMark/SkinRankMark';
import {getRankSkinFramePath} from '../../../utils/factions/getRankSkinFramePath';
import {getFactionSkinRank} from '../../../utils/factions/getFactionSkinRank';
import {getFactionSkinAccentColorKey} from '../../../utils/factions/getFactionSkinAccentColorKey';
import {buildRankSkinItems} from './buildRankSkinItems';
import PageButton from '../../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {API_BASE} from '../../../utils/apiConfig';
import {getChestIconPath} from '../../../utils/inventory/getChestIconPath';
import {getChestDisplayName} from '../../../utils/inventory/getChestDisplayName';
import {getFactionIconPath} from '../../../utils/factions/getFactionIconPath';
import {getFactionRankIconPath} from '../../../utils/factions/getFactionRankIconPath';
import {
  ALL_FACTION_SKINS,
  getFactionSkinIconPath,
  isFactionSkinVisible
} from '../../../configs/factions/skins/allFactionSkins';
import {FACTION_CONFIGS_IMPLEMENTED} from '../../../configs/factions';
import {UNIT_TYPES} from '../../../configs/units';
import {getFactionUnitSkinIconPath} from '../../../utils/factions/getFactionUnitSkinIconPath';
import {COLORS_CONFIG, PLAYER_COLORS} from '../../../configs/colors';
import {
  setAvailableFactions,
  setIsAllFactionsAvailable,
  setPremiumExpiresAt,
  updatePlayerRewards
} from '../../../redux/actions';
import {INVENTORY_ITEMS} from '../../../constants/inventoryItems';
import FactionSkinRewardModal from '../../Modals/FactionSkinRewardModal/FactionSkinRewardModal';
import EternalChestRewardModal from '../../Modals/EternalChestRewardModal/EternalChestRewardModal';
import FactionPurchaseRewardModal from '../../Modals/FactionPurchaseRewardModal/FactionPurchaseRewardModal';
import FactionUnitSkinRewardModal from '../../Modals/FactionUnitSkinRewardModal/FactionUnitSkinRewardModal';
import PremiumRewardModal from '../../Modals/PremiumRewardModal/PremiumRewardModal';
import {CHESTS} from '../../../constants/chests';
import {useSortedFactionConfigs} from "../../../hooks/factions/useSortedFactionConfigs";
import {RANK_NAME_COLORS} from "../../../constants/factions";
import {getRankSkinId, getRankSkinLeague} from "../../../configs/rankSkins";
import {getCachedOrFetch} from "../../../utils/fetchCache";
import {LoadedImage, useIsImageLoaded} from '../../EmblaCarousel/FactionsCarousel';
import {playFactionSound, useFactionHoverSound} from "../../../hooks/sounds/useFactionHoverSound";
import {MENU_SOUND_MANAGER} from "../../../scenes/MenuSoundManager";
import {addClassNameIfTrue} from "../../../utils/other/addClassNameIfTrue";
import {useScrollIntoViewWhenReady} from '../../../hooks/visuals/useScrollIntoViewWhenReady';
import {INVENTORY_FOCUS_SECTIONS} from '../../../constants/inventoryFocusSections';

function resolveFactionColor(colorKey, fallbackColor) {
  if (colorKey === PLAYER_COLORS.BROWN) return '#7b3c00';
  if (colorKey === PLAYER_COLORS.BLUE) return '#157be0';
  return COLORS_CONFIG[colorKey] || fallbackColor;
}

function getFactionSkinSelectionColor(faction, selectedSkinId, useFactionColorAsDefault) {
  if (!selectedSkinId || selectedSkinId === `${faction.id}_default`) {
    return useFactionColorAsDefault ? faction.textColor : undefined;
  }
  const rank = getRankSkinLeague(selectedSkinId);
  if (rank && selectedSkinId === getRankSkinId(faction.id, rank)) {
    return RANK_NAME_COLORS[rank];
  }
  const skinConfig = ALL_FACTION_SKINS.find((skin) => skin.id === selectedSkinId);
  const colorKey = getFactionSkinAccentColorKey(skinConfig, faction);
  return resolveFactionColor(colorKey, faction.textColor);
}

const CHEST_ORDER = [
  CHESTS.ETERNAL,
  CHESTS.RANDOM_LEGENDARY,
  CHESTS.RANDOM_EPIC,
  CHESTS.RANDOM_DEFAULT,
  CHESTS.SKINS_GOLD,
  CHESTS.SKINS_SILVER,
  CHESTS.SKINS_IRON,
  CHESTS.UNIT_SKINS_GOLD,
  CHESTS.UNIT_SKINS_SILVER,
  CHESTS.UNIT_SKINS_IRON,
  CHESTS.GOD,
  CHESTS.RUBY,
  CHESTS.DIAMOND,
  CHESTS.GOLD,
  CHESTS.SILVER,
  CHESTS.BRONZE,
  CHESTS.WOODEN,
];

function playButtonClickSound() {
  const soundManager = MENU_SOUND_MANAGER;
  if (soundManager?.playMenuButtonClick) {
    soundManager.playMenuButtonClick();
  }
}

function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function useGridColumns(ref, deps) {
  const [columns, setColumns] = useState(4);

  useEffect(() => {
    if (!ref.current) return;

    const grid = ref.current;

    const updateColumns = () => {
      const style = getComputedStyle(grid);
      const templateColumns = style.gridTemplateColumns;
      const cols = templateColumns ? templateColumns.split(' ').length : 1;
      setColumns(cols);
    };

    updateColumns();

    const resizeObserver = new ResizeObserver(updateColumns);
    resizeObserver.observe(grid);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return columns;
}

function getFactionInventoryIconPath(factionId, selectedSkinId) {
  if (!selectedSkinId || selectedSkinId === `${factionId}_default`) {
    return getFactionIconPath(factionId);
  }
  const rank = getRankSkinLeague(selectedSkinId);
  if (rank && selectedSkinId === getRankSkinId(factionId, rank)) {
    return getFactionRankIconPath(factionId, rank);
  }
  return getFactionSkinIconPath(selectedSkinId);
}

export function FactionInventoryItem({faction, selectedSkinId, selectionColor, onClick, lastPlayedByFactionRef}) {
  const {
    isHovered,
    isLongHovered,
    onMouseEnter,
    onMouseLeave
  } = useFactionHoverSound(faction.id, lastPlayedByFactionRef);

  const iconPath = useMemo(
    () => getFactionInventoryIconPath(faction.id, selectedSkinId),
    [faction.id, selectedSkinId]
  );

  const handleClick = () => {
    const played = playFactionSound(faction.id, lastPlayedByFactionRef);
    if (!played) {
      playButtonClickSound();
    }
    onClick?.();
  };

  const isRankSkin = !!getRankSkinFramePath(selectedSkinId);

  return (
    <div
      className={`inventory-item inventory-faction-item ${faction.isAvailable ? '' : 'inventory-faction-item_unavailable'} ${isHovered ? 'inventory-faction-item_hovered' : ''} ${isLongHovered ? 'inventory-faction-item_long-hovered' : ''} ${selectionColor ? 'inventory-faction-item_selected' : ''} ${addClassNameIfTrue(isRankSkin, 'inventory-item-no-border')}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      style={selectionColor ? {backgroundColor: selectionColor} : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <LoadedImage
        className="inventory-faction-icon"
        src={iconPath}
        alt={faction.name}
        placeholderSrc="/img/icons/banner.png"
      />
      {/* Рамка рейтингового образа: у плитки фракции она показывает, что
          выбран именно ранговый образ (см. RankSkinFrame) */}
      <RankSkinFrame skinId={selectedSkinId} className="inventory-faction-frame"/>
      {/* Шевроны рейтинга выбранного обычного обличия этой фракции; у
          базового вида и рейтинговых образов их нет (см. SkinRankMark) */}
      <SkinRankMark
        skinRank={getFactionSkinRank(selectedSkinId, faction.id)}
        className="inventory-skin-rank-mark"
      />
    </div>
  );
}

// Тонированная (mask-image) иконка образа отряда со своей заглушкой-баннером
// позади — выделена отдельным компонентом, а не веткой инлайн в JSX, чтобы
// useIsImageLoaded (эффект + временный Image()-зонд) заводился только для
// реально тонированных иконок (образы отрядов), а не для каждой из десятков
// плашек в "Обличиях фракций"/"Рейтинговых обличиях", которые сюда не заходят
function TintedSkinIcon({iconPath, isAvailable, iconColor, altName}) {
  const isLoaded = useIsImageLoaded(iconPath);
  return (
    <span className="loaded-image-wrapper">
      <img
        src="/img/icons/banner.png"
        alt=""
        aria-hidden="true"
        className={`inventory-faction-icon inventory-skin-icon loaded-image-placeholder ${isLoaded ? 'loaded-image-placeholder_hidden' : ''}`}
        loading="eager"
        decoding="async"
      />
      <div
        className={`inventory-faction-icon inventory-skin-icon inventory-skin-icon_tinted loaded-image-main ${isLoaded ? 'loaded-image-main_visible' : ''}`}
        style={{
          backgroundColor: isAvailable ? iconColor : undefined,
          '--inventory-icon-hover-color': iconColor,
          WebkitMaskImage: `url(${iconPath})`,
          maskImage: `url(${iconPath})`,
        }}
        role="img"
        aria-label={altName}
      />
    </span>
  );
}

export function FactionSkinInventoryItem({
                                           skin,
                                           isAvailable,
                                           onClick,
                                           lastPlayedByFactionRef,
                                           selectionColor,
                                           iconColor,
                                           className
                                         }) {
  const {
    isHovered,
    isLongHovered,
    onMouseEnter,
    onMouseLeave
  } = useFactionHoverSound(skin.factionId, lastPlayedByFactionRef);
  const iconPath = skin.imagePath || skin.iconPath || getFactionSkinIconPath(skin.id);
  // У рангового образа вместо обычного бордюра плитки — рамка его лиги
  const rankFramePath = getRankSkinFramePath(skin.id);

  const handleClick = () => {
    const played = playFactionSound(skin.factionId, lastPlayedByFactionRef);
    if (!played) {
      playButtonClickSound();
    }
    onClick?.();
  };

  return (
    <div
      className={`inventory-item inventory-faction-item inventory-skin-item ${className} ${rankFramePath ? 'inventory-skin-item_framed' : ''} ${isAvailable ? '' : 'inventory-faction-item_unavailable'} ${isHovered ? 'inventory-faction-item_hovered' : ''} ${isLongHovered ? 'inventory-faction-item_long-hovered' : ''} ${selectionColor ? 'inventory-skin-item_selected' : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      style={selectionColor ? {backgroundColor: selectionColor} : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      {iconColor ? (
        // Иконка — однотонный белый силуэт: вместо <img> красим её через
        // mask-image (форма из PNG) + background-color, вместо тонировки
        // фона плашки. Для неоткрытых цвет не проставляется напрямую (плашка
        // остается белой/приглушенной), а только кладется в CSS-переменную —
        // сам цвет проявляется по hover тем же правилом, что снимает ч/б
        <TintedSkinIcon
          iconPath={iconPath}
          isAvailable={isAvailable}
          iconColor={iconColor}
          altName={skin.name}
        />
      ) : (
        <LoadedImage
          className="inventory-faction-icon inventory-skin-icon"
          src={iconPath}
          alt={skin.name}
          placeholderSrc="/img/icons/banner.png"
        />
      )}
      {/* Рамка лиги вместо бордюра плитки (см. RankSkinFrame) */}
      <RankSkinFrame skinId={skin.id} className="inventory-skin-frame"/>
      {/* Шевроны рейтинга обычного обличия — и у открытых, и у неоткрытых.
          Плитки рейтинговых образов и образов отрядов идут без них */}
      <SkinRankMark
        skinRank={getFactionSkinRank(skin.id, skin.factionId)}
        className="inventory-skin-rank-mark"
      />
    </div>
  );
}

function MoreSkinsItem({count, onClick}) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    playButtonClickSound();
    onClick?.();
  };

  return (
    <div
      className={`inventory-item inventory-faction-item inventory-skin-item inventory-more-skins-item ${isHovered ? 'inventory-faction-item_hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <span className="inventory-more-skins-text">
        ЕЩЕ
        <br/>
        {count}
      </span>
    </div>
  );
}

function LimitedSkinGrid({gridRef, items, renderItem, onMoreClick}) {
  const columns = useGridColumns(gridRef, [items.length]);
  const maxRows = 3;
  const cellCount = maxRows * columns;

  const showMore = items.length > cellCount;
  const visibleItems = showMore ? items.slice(0, cellCount - 1) : items;
  const hiddenCount = showMore ? items.length - visibleItems.length : 0;
  const mockCount = showMore
    ? 0
    : (visibleItems.length > 0
      ? (columns - (visibleItems.length % columns)) % columns
      : 0);

  return (
    <div className="inventory-grid" ref={gridRef}>
      {visibleItems.map((item, index) => renderItem(item, index))}
      {showMore && <MoreSkinsItem count={hiddenCount} onClick={onMoreClick}/>}
      {Array.from({length: mockCount}).map((_, index) => (
        <div
          key={`limited-mock-${index}`}
          className="inventory-faction-item inventory-faction-item_mock"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

function ChestRewardOverlay({reward}) {
  if (!reward) return null;

  if (reward.type === 'numens' || reward.itemType === INVENTORY_ITEMS.NUMENS) {
    return (
      <div className="inventory-chest-reward-overlay">
        <span className="inventory-chest-reward-amount text-coins">{reward.amount}</span>
        <img className="inventory-chest-reward-icon" src="/img/icons/numens.png" alt="нумены"/>
      </div>
    );
  }

  if (reward.type === 'faction_skin' || reward.itemType === INVENTORY_ITEMS.FACTION_SKIN) {
    return (
      <div className="inventory-chest-reward-overlay">
        <img
          className="inventory-chest-reward-skin-icon"
          src={getFactionSkinIconPath(reward.skinId)}
          alt={reward.skinName || reward.skinId}
        />
      </div>
    );
  }

  if (reward.type === 'crystals' || reward.itemType === INVENTORY_ITEMS.CRYSTALS) {
    return (
      <div className="inventory-chest-reward-overlay">
        <span className="inventory-chest-reward-amount text-crystals">{reward.amount}</span>
        <img className="inventory-chest-reward-icon" src="/img/icons/crystal.png" alt="кристаллы"/>
      </div>
    );
  }

  if (reward.type === 'faction' || reward.itemType === INVENTORY_ITEMS.FACTION) {
    return (
      <div className="inventory-chest-reward-overlay">
        <img
          className="inventory-chest-reward-skin-icon"
          src={getFactionIconPath(reward.factionId)}
          alt={reward.factionName || reward.factionId}
        />
      </div>
    );
  }

  if (reward.type === 'premium' || reward.itemType === INVENTORY_ITEMS.PREMIUM) {
    return (
      <div className="inventory-chest-reward-overlay">
        <span className="inventory-chest-reward-amount text-coins">{reward.days}</span>
        <img className="inventory-chest-reward-icon" src="/img/icons/premium.png" alt="Премиум"/>
      </div>
    );
  }

  if (reward.type === 'faction_unit_skin' || reward.itemType === INVENTORY_ITEMS.FACTION_UNIT_SKIN) {
    return (
      <div className="inventory-chest-reward-overlay">
        <img
          className="inventory-chest-reward-skin-icon"
          src={getFactionIconPath(reward.factionId)}
          alt="Образ отрядов"
        />
      </div>
    );
  }

  if (reward.type === 'nothing') {
    const label = reward.reason === 'all_factions_owned'
      ? 'У вас уже есть все фракции'
      : reward.reason === 'all_unit_skins_owned'
        ? 'У вас уже есть все образы отрядов'
        : 'У вас уже есть все скины';
    return (
      <div className="inventory-chest-reward-overlay">
        <span className="inventory-chest-reward-label">{label}</span>
      </div>
    );
  }

  if (reward.type === 'all_factions' || reward.itemType === INVENTORY_ITEMS.ALL_FACTIONS) {
    return (
      <div className="inventory-chest-reward-overlay">
        <span className="inventory-chest-reward-label">Все фракции открыты!</span>
      </div>
    );
  }

  return (
    <div className="inventory-chest-reward-overlay">
      <span className="inventory-chest-reward-label">Неизвестная награда</span>
    </div>
  );
}

function ChestInventoryItem({
                              chestId,
                              uniqueId,
                              onOpen,
                              onRemove,
                              onSkinReward,
                              onAllFactionsReward,
                              onFactionReward,
                              onUnitSkinReward,
                              onPremiumReward
                            }) {
  const [phase, setPhase] = useState('idle');
  const [reward, setReward] = useState(null);
  const timersRef = useRef([]);

  const clearAllTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => clearAllTimers, []);

  const startRemove = () => {
    clearAllTimers();
    setPhase('removing');
    const removeTimer = setTimeout(() => {
      onRemove(uniqueId);
    }, 500);
    timersRef.current.push(removeTimer);
  };

  const handleClick = async () => {
    if (phase === 'opening') return;

    if (phase === 'revealed') {
      if (reward?.type === 'faction_skin' || reward?.itemType === INVENTORY_ITEMS.FACTION_SKIN) {
        onSkinReward?.(reward.skinId);
      } else if (reward?.type === 'all_factions' || reward?.itemType === INVENTORY_ITEMS.ALL_FACTIONS) {
        onAllFactionsReward?.();
      } else if (reward?.type === 'faction' || reward?.itemType === INVENTORY_ITEMS.FACTION) {
        onFactionReward?.(reward.factionId);
      } else if (reward?.type === 'faction_unit_skin' || reward?.itemType === INVENTORY_ITEMS.FACTION_UNIT_SKIN) {
        onUnitSkinReward?.(reward.unitSkinId, reward.factionId);
      } else if (reward?.type === 'premium' || reward?.itemType === INVENTORY_ITEMS.PREMIUM) {
        onPremiumReward?.(reward.days);
      } else {
        startRemove();
      }
      return;
    }

    if (phase !== 'idle') return;

    const soundManager = MENU_SOUND_MANAGER;
    if (soundManager?.playChestOpen) {
      soundManager.playChestOpen();
    }

    setPhase('opening');

    try {
      const rewardResult = await onOpen(chestId);
      setReward(rewardResult);

      const isNumensReward = rewardResult?.type === 'numens' || rewardResult?.itemType === INVENTORY_ITEMS.NUMENS;
      const isCrystalsReward = rewardResult?.type === 'crystals' || rewardResult?.itemType === INVENTORY_ITEMS.CRYSTALS;
      const isSkinReward = rewardResult?.type === 'faction_skin' || rewardResult?.itemType === INVENTORY_ITEMS.FACTION_SKIN;
      const isAllFactionsReward = rewardResult?.type === 'all_factions' || rewardResult?.itemType === INVENTORY_ITEMS.ALL_FACTIONS;
      const isFactionReward = rewardResult?.type === 'faction' || rewardResult?.itemType === INVENTORY_ITEMS.FACTION;
      const isUnitSkinReward = rewardResult?.type === 'faction_unit_skin' || rewardResult?.itemType === INVENTORY_ITEMS.FACTION_UNIT_SKIN;
      const isPremiumReward = rewardResult?.type === 'premium' || rewardResult?.itemType === INVENTORY_ITEMS.PREMIUM;
      const skinRewardId = isSkinReward ? rewardResult.skinId : null;
      const revealDelay = 1000;

      const revealTimer = setTimeout(() => {
        setPhase('revealed');

        if (isNumensReward || isCrystalsReward) {
          if (soundManager?.playCoins) {
            soundManager.playCoins(rewardResult.amount);
          }
          const autoRemoveTimer = setTimeout(() => {
            startRemove();
          }, 2000);
          timersRef.current.push(autoRemoveTimer);
        } else if (isSkinReward && skinRewardId) {
          const modalTimer = setTimeout(() => {
            onSkinReward?.(skinRewardId, uniqueId);
          }, 1500);
          timersRef.current.push(modalTimer);
        } else if (isAllFactionsReward) {
          const modalTimer = setTimeout(() => {
            onAllFactionsReward?.(uniqueId);
          }, 1500);
          timersRef.current.push(modalTimer);
        } else if (isFactionReward && rewardResult.factionId) {
          const modalTimer = setTimeout(() => {
            onFactionReward?.(rewardResult.factionId, uniqueId);
          }, 1500);
          timersRef.current.push(modalTimer);
        } else if (isUnitSkinReward && rewardResult.unitSkinId) {
          const modalTimer = setTimeout(() => {
            onUnitSkinReward?.(rewardResult.unitSkinId, rewardResult.factionId, uniqueId);
          }, 1500);
          timersRef.current.push(modalTimer);
        } else if (isPremiumReward && rewardResult.days) {
          const modalTimer = setTimeout(() => {
            onPremiumReward?.(rewardResult.days, uniqueId);
          }, 1500);
          timersRef.current.push(modalTimer);
        } else {
          const autoRemoveTimer = setTimeout(() => {
            startRemove();
          }, 2000);
          timersRef.current.push(autoRemoveTimer);
        }
      }, revealDelay);

      timersRef.current.push(revealTimer);
    } catch (e) {
      console.error('Chest open failed', e);
      alert(e.message || 'Не удалось открыть сундук');
      setPhase('idle');
    }
  };

  const phaseClass = phase === 'opening'
    ? 'inventory-chest-item_opening'
    : phase === 'revealed'
      ? 'inventory-chest-item_revealed'
      : phase === 'removing'
        ? 'inventory-chest-item_removing'
        : '';

  return (
    <div
      className={`inventory-item inventory-chest-item ${phaseClass}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <LoadedImage
        className="inventory-chest-icon"
        src={getChestIconPath(chestId)}
        alt={getChestDisplayName(chestId)}
        placeholderSrc="/img/icons/chest.png"
      />
      {phase === 'revealed' && <ChestRewardOverlay reward={reward}/>}
    </div>
  );
}

/**
 * Коллекция игрока.
 *
 * Своя коллекция берёт фракции и выбранные обличия из стора (они там уже есть,
 * лишний запрос не нужен) и догружает сундуки и образы.
 *
 * Чужая (userId задан) целиком приходит одним запросом и показывается только
 * для просмотра: без сундуков и без переходов в редактирование фракций
 */
function InventoryPage({onBack, onFactionClick, onAllSkinsClick, userId, userName, focusSection}) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.private.auth);
  // Чужой профиль: всё только смотрим
  const isForeignUser = Boolean(userId);
  const [foreignData, setForeignData] = useState(null);
  const factionLoadouts = useMemo(
    () => (isForeignUser ? foreignData?.factionLoadouts : auth.factionLoadouts) || {},
    [isForeignUser, foreignData, auth.factionLoadouts]
  );
  const chestGridRef = useRef(null);
  const factionGridRef = useRef(null);
  const unitSkinGridRef = useRef(null);
  const skinGridRef = useRef(null);
  const rankSkinGridRef = useRef(null);
  const rankSkinsTitleRef = useRef(null);
  // Секция, к которой открыли коллекцию (см. INVENTORY_FOCUS_SECTIONS), —
  // на момент открытия: App сбрасывает ее, как только игрок уходит со
  // страницы, и прокрутка не должна зависеть от этого
  const [initialFocusSection] = useState(focusSection);
  const lastFactionSoundTimeRef = useRef({});
  const [inventory, setInventory] = useState({chests: []});
  const [chestItems, setChestItems] = useState([]);
  const [availableSkinIds, setAvailableSkinIds] = useState([]);
  const [availableUnitSkinIds, setAvailableUnitSkinIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Секции рисуются только после загрузки коллекции — тогда и прокручиваем
  useScrollIntoViewWhenReady(rankSkinsTitleRef, {
    isEnabled: initialFocusSection === INVENTORY_FOCUS_SECTIONS.RANK_SKINS,
    isReady: !loading && !error,
  });
  const [skinReward, setSkinReward] = useState(null);
  const [eternalChestReward, setEternalChestReward] = useState(null);
  const [factionReward, setFactionReward] = useState(null);
  const [unitSkinReward, setUnitSkinReward] = useState(null);
  const [premiumReward, setPremiumReward] = useState(null);
  const hasFetchedRef = useRef(false);

  const factions = useMemo(
    () => (isForeignUser ? foreignData?.factions : auth.availableFactions) || [],
    [isForeignUser, foreignData, auth.availableFactions]
  );

  const chestColumns = useGridColumns(chestGridRef, [loading, chestItems.length]);
  const factionColumns = useGridColumns(factionGridRef, [loading, factions.length]);
  const unitSkinColumns = useGridColumns(unitSkinGridRef, [loading, availableUnitSkinIds.length]);

  usePageAnalytics('InventoryPage');

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchForeignData = async () => {
      try {
        const res = await fetch(`${API_BASE}/users/${userId}/inventory`, {
          method: 'GET',
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to fetch user inventory');
        const data = await res.json();

        setForeignData(data);
        setAvailableSkinIds(data.skins || []);
        setAvailableUnitSkinIds(data.unitSkins || []);
      } catch (e) {
        console.error('User inventory fetch error', e);
        setError('Не удалось загрузить коллекцию');
      } finally {
        setLoading(false);
      }
    };

    const fetchData = async () => {
      try {
        const [inventoryData, skinsData] = await Promise.all([
          getCachedOrFetch('inventory', () => fetch(`${API_BASE}/inventory`, {
            method: 'GET',
            credentials: 'include',
          }).then(async (res) => {
            if (!res.ok) throw new Error('Failed to fetch inventory');
            return res.json();
          })),
          getCachedOrFetch('available-skins', () => fetch(`${API_BASE}/available-skins`, {
            method: 'GET',
            credentials: 'include',
          }).then(async (res) => {
            if (!res.ok) throw new Error('Failed to fetch skins');
            return res.json();
          })),
        ]);

        setInventory(inventoryData.inventory || {chests: []});
        setAvailableSkinIds(skinsData.skins || []);
        setAvailableUnitSkinIds(inventoryData.inventory?.unitSkins || []);
        dispatch(updatePlayerRewards(0, undefined, false));
      } catch (e) {
        console.error('Inventory fetch error', e);
        setError('Не удалось загрузить инвентарь');
      } finally {
        setLoading(false);
      }
    };

    if (isForeignUser) {
      fetchForeignData();
    } else {
      fetchData();
    }
  }, [dispatch, isForeignUser, userId]);

  useEffect(() => {
    if (loading) return;
    setChestItems((prev) => {
      if (prev.length > 0) return prev;
      return (inventory.chests || [])
        .filter((item) => typeof item === 'string' && item.startsWith('chest_'))
        .map((chestId) => ({
          chestId,
          uniqueId: `${chestId}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        }))
        .sort((a, b) => {
          const aIndex = CHEST_ORDER.indexOf(a.chestId);
          const bIndex = CHEST_ORDER.indexOf(b.chestId);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
    });
  }, [loading, inventory.chests]);

  const handleChestOpen = async (chestId) => {
    const response = await fetch(`${API_BASE}/open-chest`, {
      method: 'POST',
      credentials: 'include',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({chestId}),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to open chest');
    }

    const data = await response.json();

    if (data.reward?.type === 'numens' || data.reward?.itemType === INVENTORY_ITEMS.NUMENS) {
      if (data.reward.amount) {
        dispatch(updatePlayerRewards(data.reward.amount, undefined, false));
      }
    }

    if (data.reward?.type === 'crystals' || data.reward?.itemType === INVENTORY_ITEMS.CRYSTALS) {
      if (data.reward.amount) {
        dispatch(updatePlayerRewards(0, undefined, false, data.reward.amount));
      }
    }

    if (data.reward?.type === 'faction_skin' || data.reward?.itemType === INVENTORY_ITEMS.FACTION_SKIN) {
      if (data.reward.skinId) {
        setAvailableSkinIds((prev) => [...prev, data.reward.skinId]);
      }
    }

    if (data.reward?.type === 'faction_unit_skin' || data.reward?.itemType === INVENTORY_ITEMS.FACTION_UNIT_SKIN) {
      if (data.reward.unitSkinId) {
        setAvailableUnitSkinIds((prev) => [...prev, data.reward.unitSkinId]);
      }
    }

    if (data.reward?.type === 'faction' || data.reward?.itemType === INVENTORY_ITEMS.FACTION) {
      if (data.reward.factionId) {
        dispatch(setAvailableFactions([
          ...factions,
          {id: data.reward.factionId, name: data.reward.factionName, isAvailable: true},
        ]));
      }
    }

    // Премиум сервер уже начислил (продлив от остатка) и вернул итоговую дату —
    // обновляем профиль, чтобы иконка премиума и его бонусы включились сразу
    if (data.reward?.type === 'premium' || data.reward?.itemType === INVENTORY_ITEMS.PREMIUM) {
      if (data.reward.premiumExpiresAt) {
        dispatch(setPremiumExpiresAt(data.reward.premiumExpiresAt));
      }
    }

    if (data.reward?.type === 'all_factions' || data.reward?.itemType === INVENTORY_ITEMS.ALL_FACTIONS) {
      dispatch(setAvailableFactions(FACTION_CONFIGS_IMPLEMENTED.map((config) => ({
        id: config.id,
        name: config.name,
        isAvailable: true,
      }))));
      dispatch(setIsAllFactionsAvailable(true));
    }

    setInventory((prev) => {
      const chests = [...(prev.chests || [])];
      const index = chests.indexOf(chestId);
      if (index !== -1) {
        chests.splice(index, 1);
      }
      return {...prev, chests};
    });

    return data.reward;
  };

  const handleChestRemove = (uniqueId) => {
    setChestItems((prev) => prev.filter((item) => item.uniqueId !== uniqueId));
  };

  const handleSkinReward = (skinId, uniqueId) => {
    setSkinReward({skinId, uniqueId});
  };

  const handleSkinRewardEntered = () => {
    if (skinReward?.uniqueId) {
      handleChestRemove(skinReward.uniqueId);
    }
  };

  const handleCloseSkinReward = () => {
    setSkinReward(null);
  };

  const handleAllFactionsReward = (uniqueId) => {
    setEternalChestReward({uniqueId});
  };

  const handleEternalChestRewardEntered = () => {
    if (eternalChestReward?.uniqueId) {
      handleChestRemove(eternalChestReward.uniqueId);
    }
  };

  const handleCloseEternalChestReward = () => {
    setEternalChestReward(null);
  };

  const handleFactionReward = (factionId, uniqueId) => {
    setFactionReward({factionId, uniqueId});
  };

  const handleFactionRewardEntered = () => {
    if (factionReward?.uniqueId) {
      handleChestRemove(factionReward.uniqueId);
    }
  };

  const handleCloseFactionReward = () => {
    setFactionReward(null);
  };

  const handlePremiumReward = (days, uniqueId) => {
    setPremiumReward({days, uniqueId});
  };

  const handlePremiumRewardEntered = () => {
    if (premiumReward?.uniqueId) {
      handleChestRemove(premiumReward.uniqueId);
    }
  };

  const handleClosePremiumReward = () => {
    setPremiumReward(null);
  };

  const handleUnitSkinReward = (unitSkinId, factionId, uniqueId) => {
    setUnitSkinReward({unitSkinId, factionId, uniqueId});
  };

  const handleUnitSkinRewardEntered = () => {
    if (unitSkinReward?.uniqueId) {
      handleChestRemove(unitSkinReward.uniqueId);
    }
  };

  const handleCloseUnitSkinReward = () => {
    setUnitSkinReward(null);
  };

  const chestMockCount = chestItems.length > 0
    ? (chestColumns - (chestItems.length % chestColumns)) % chestColumns
    : 0;

  const sortedFactions = useSortedFactionConfigs(factions);

  const handleAllSkinsClick = () => {
    onAllSkinsClick?.(factions, availableSkinIds, factionLoadouts, availableUnitSkinIds);
  };

  // Страница фракции в чужой коллекции показывает ЕГО выбранное обличие и его
  // образы отрядов — поэтому передаем их вместе с переходом
  const handleFactionClick = (factionId, skinId, focusUnitSkins) => {
    onFactionClick?.(factionId, skinId, factions, availableSkinIds, focusUnitSkins, {
      factionLoadouts,
      unitSkinIds: availableUnitSkinIds,
    });
  };

  const factionMockCount = sortedFactions.length > 0
    ? (factionColumns - (sortedFactions.length % factionColumns)) % factionColumns
    : 0;

  // Образ отряда фракции хранится на аккаунте одной записью на фракцию
  // (unit_skins_<factionId>), а не по типу отряда — иконка выбирается для
  // d20 (сильнейший стандартный отряд), либо d10, если у фракции нет d20
  // в перечне доступных типов (например, Духи — только d10).
  // Порядок плашек — тот же, что и у самих фракций (sortedFactions), а не
  // отдельная own/unowned-сортировка
  const factionUnitSkinItems = useMemo(() => {
    const ownedUnitSkinIdsSet = new Set(availableUnitSkinIds || []);
    return sortedFactions.map((faction) => {
      const hasD20 = !faction.unitTypes || faction.unitTypes.includes(UNIT_TYPES.D20.id);
      const unitType = hasD20 ? UNIT_TYPES.D20 : UNIT_TYPES.D10;
      const unitSkinId = `unit_skins_${faction.id}`;
      const isOwned = ownedUnitSkinIdsSet.has(unitSkinId);
      const factionSelectedSkinId = factionLoadouts[faction.id]?.selectedSkin || `${faction.id}_default`;
      return {
        id: unitSkinId,
        factionId: faction.id,
        selectedSkinId: factionSelectedSkinId,
        name: `${faction.name} — образ отряда`,
        imagePath: getFactionUnitSkinIconPath(unitType.id, faction.id),
        isOwned,
        // Цвет считаем всегда (та же логика, что и у плашки фракции выше:
        // цвет выбранного скина, а без выбора — цвет самой фракции).
        // Для открытых иконка красится сразу; для неоткрытых — только по
        // hover (см. FactionSkinInventoryItem), в остальное время белая/ч-б
        iconColor: getFactionSkinSelectionColor(faction, factionSelectedSkinId, true),
      };
    });
  }, [sortedFactions, availableUnitSkinIds, factionLoadouts]);

  const unitSkinItemCount = 1 + factionUnitSkinItems.length; // +1 — плашка стандартного образа
  const unitSkinMockCount = (unitSkinColumns - (unitSkinItemCount % unitSkinColumns)) % unitSkinColumns;

  const sortedSkins = useMemo(() => {
    const ownedSkinIdsSet = new Set(availableSkinIds || []);
    const visibleSkins = ALL_FACTION_SKINS.filter((skin) => isFactionSkinVisible(skin, ownedSkinIdsSet));
    const ownedSkins = visibleSkins.filter((skin) => ownedSkinIdsSet.has(skin.id));
    const unavailableSkins = visibleSkins.filter((skin) => !ownedSkinIdsSet.has(skin.id));
    return [...ownedSkins, ...shuffleArray(unavailableSkins)];
  }, [availableSkinIds]);
  const rankSkins = useMemo(
    () => buildRankSkinItems(factions, availableSkinIds),
    [factions, availableSkinIds]
  );


  return (
    <>
      <PageButton
        className="inventory-floating-back-button"
        onClick={onBack}
        pageId="InventoryPage"
        buttonId="back_floating"
      >
        <img
          className="inventory-floating-back-button_icon"
          src="/img/icons/back_navigation.png"
          alt="Назад"
          title="Назад"
        />
      </PageButton>

      <h1 className="lobby-page-title inventory-page-title">
        {isForeignUser ? (
          <>Коллекция<br/>{userName || foreignData?.name || ''}</>
        ) : (
          <>Моя<br className="inventory-page-title-mobile-break"/> Коллекция</>
        )}
      </h1>

      {loading && <p className="inventory-status">Загрузка...</p>}
      {error && <p className="inventory-status inventory-status_error">{error}</p>}

      {!loading && !error && (
        <>
          {/* Сундуки — только свои: чужие ещё не открыты, и открывать их нельзя */}
          {!isForeignUser && (
            <>
              <h2 className={'inventory-page-title_h2'}>Сундуки</h2>

              {chestItems.length === 0 ? (
                <p className="inventory-status inventory_no-items">У вас пока нет сундуков</p>
              ) : (
                <div className="inventory-grid" ref={chestGridRef}>
                  {chestItems.map((item) => (
                    <ChestInventoryItem
                      key={item.uniqueId}
                      chestId={item.chestId}
                      uniqueId={item.uniqueId}
                      onOpen={handleChestOpen}
                      onRemove={handleChestRemove}
                      onSkinReward={handleSkinReward}
                      onAllFactionsReward={handleAllFactionsReward}
                      onFactionReward={handleFactionReward}
                      onUnitSkinReward={handleUnitSkinReward}
                      onPremiumReward={handlePremiumReward}
                    />
                  ))}
                  {Array.from({length: chestMockCount}).map((_, index) => (
                    <div
                      key={`chest-mock-${index}`}
                      className="inventory-chest-item inventory-chest-item_mock"
                      aria-hidden="true"
                    />
                  ))}
                </div>
              )}
            </>
          )}

          <h2 className={'inventory-page-title_h2'}>Фракции</h2>

          {sortedFactions.length === 0 ? (
            <p className="inventory-status inventory_no-items">Нет данных о фракциях</p>
          ) : (
            <div className="inventory-grid" ref={factionGridRef}>
              {sortedFactions.map((faction) => {
                const factionSelectedSkinId = factionLoadouts[faction.id]?.selectedSkin || `${faction.id}_default`;
                const selectionColor = getFactionSkinSelectionColor(faction, factionSelectedSkinId);
                return (
                  <FactionInventoryItem
                    key={faction.id}
                    faction={faction}
                    selectedSkinId={factionSelectedSkinId}
                    selectionColor={selectionColor}
                    lastPlayedByFactionRef={lastFactionSoundTimeRef}
                    onClick={() => handleFactionClick(faction.id, factionSelectedSkinId)}
                  />
                );
              })}
              {Array.from({length: factionMockCount}).map((_, index) => (
                <div
                  key={`faction-mock-${index}`}
                  className="inventory-faction-item inventory-faction-item_mock"
                  aria-hidden="true"
                />
              ))}
            </div>
          )}

          <h2 className={'inventory-page-title_h2'}>Образы отрядов фракций</h2>

          <div className="inventory-grid" ref={unitSkinGridRef}>
            {factionUnitSkinItems.map((item) => (
              <FactionSkinInventoryItem
                key={item.id}
                skin={item}
                isAvailable={item.isOwned}
                iconColor={item.iconColor}
                lastPlayedByFactionRef={lastFactionSoundTimeRef}
                className={'inventory-unit-skin-item'}
                onClick={() => handleFactionClick(item.factionId, item.selectedSkinId, true)}
              />
            ))}
            <div
              className="inventory-item inventory-faction-item inventory-skin-item inventory-unit-skin-item"
              title="Стандартный образ отряда (используется по умолчанию, пока не открыт индивидуальный)"
            >
              <LoadedImage
                className="inventory-faction-icon inventory-skin-icon"
                src="/img/icons/unit d20 white.png"
                alt="Стандартный образ отряда"
                placeholderSrc="/img/icons/banner.png"
              />
            </div>
            {Array.from({length: unitSkinMockCount}).map((_, index) => (
              <div
                key={`unit-skin-mock-${index}`}
                className="inventory-faction-item inventory-faction-item_mock"
                aria-hidden="true"
              />
            ))}
          </div>

          <h2 className={'inventory-page-title_h2'}>Обличия фракций</h2>

          {sortedSkins.length === 0 ? (
            <p className="inventory-status inventory_no-items">Нет данных о скинах</p>
          ) : (
            <LimitedSkinGrid
              gridRef={skinGridRef}
              items={sortedSkins}
              onMoreClick={handleAllSkinsClick}
              renderItem={(skin) => (
                <FactionSkinInventoryItem
                  key={skin.id}
                  skin={skin}
                  isAvailable={availableSkinIds.includes(skin.id)}
                  lastPlayedByFactionRef={lastFactionSoundTimeRef}
                  onClick={() => handleFactionClick(skin.factionId, skin.id)}
                />
              )}
            />
          )}

          <h2
            className={'inventory-page-title_h2 inventory-page-title_h2_focus-target'}
            ref={rankSkinsTitleRef}
          >
            Рейтинговые обличия
          </h2>

          {rankSkins.length === 0 ? (
            <p className="inventory-status inventory_no-items">Нет данных о рейтинговых скинах</p>
          ) : (
            <LimitedSkinGrid
              gridRef={rankSkinGridRef}
              items={rankSkins}
              onMoreClick={handleAllSkinsClick}
              renderItem={(skin) => (
                <FactionSkinInventoryItem
                  key={skin.id}
                  skin={skin}
                  isAvailable={skin.isAvailable}
                  lastPlayedByFactionRef={lastFactionSoundTimeRef}
                  onClick={() => handleFactionClick(skin.factionId, skin.id)}
                />
              )}
            />
          )}
        </>
      )}

      {skinReward?.skinId && (
        <FactionSkinRewardModal
          skinId={skinReward.skinId}
          onClaim={handleCloseSkinReward}
          onEntered={handleSkinRewardEntered}
        />
      )}

      {eternalChestReward && (
        <EternalChestRewardModal
          onClaim={handleCloseEternalChestReward}
          onEntered={handleEternalChestRewardEntered}
        />
      )}

      {factionReward?.factionId && (
        <FactionPurchaseRewardModal
          factionId={factionReward.factionId}
          onClaim={handleCloseFactionReward}
          onEntered={handleFactionRewardEntered}
        />
      )}

      {premiumReward?.days && (
        <PremiumRewardModal
          days={premiumReward.days}
          onClaim={handleClosePremiumReward}
          onEntered={handlePremiumRewardEntered}
        />
      )}

      {unitSkinReward?.unitSkinId && (
        <FactionUnitSkinRewardModal
          factionId={unitSkinReward.factionId}
          onClaim={handleCloseUnitSkinReward}
          onEntered={handleUnitSkinRewardEntered}
          onGoToFaction={() => {
            const rewardFactionId = unitSkinReward.factionId;
            const rewardSelectedSkinId = factionLoadouts[rewardFactionId]?.selectedSkin;
            handleCloseUnitSkinReward();
            handleFactionClick(rewardFactionId, rewardSelectedSkinId, true);
          }}
        />
      )}

      <div className="main-menu-buttons inventory-page-buttons">
        <PageButton
          className="main-menu-button" onClick={onBack} pageId="InventoryPage" buttonId="back"
        >Назад</PageButton>
      </div>
    </>
  );
}

export default InventoryPage;
